import { ReactNode } from 'react';
import {
  TextField,
  Label,
  Input,
  Text as AriaText,
  FieldError,
  type TextFieldProps,
} from 'react-aria-components';
import { cx } from './cx';
import { typography } from './Text';

const boxBase =
  'flex items-center gap-2 px-3 rounded-lg bg-surface border border-strong shadow-xs ' +
  'text-fg transition ' +
  'focus-within:border-brand-300 focus-within:ring-4 focus-within:ring-[rgb(var(--c-ring)/0.24)] ' +
  'group-data-[invalid]:border-error-500 group-data-[invalid]:focus-within:ring-error-500/24';

export interface TextInputProps extends Omit<
  TextFieldProps,
  'children' | 'className'
> {
  label?: string;
  placeholder?: string;
  /** Leading adornment, typically a 16px icon (e.g. Search). */
  iconLeading?: ReactNode;
  hint?: ReactNode;
  /** Error message; presence also drives the invalid styling. */
  errorMessage?: string;
  size?: 'sm' | 'md';
  fullWidth?: boolean;
  className?: string;
}

/**
 * The Regask text input — react-aria `TextField`, Untitled UI anatomy: a
 * top label, a bordered surface box with an xs shadow that lifts to a brand
 * focus ring, an optional leading icon, and hint / error text. Used for both
 * form fields and search (pass `iconLeading`).
 */
export const TextInput = ({
  label,
  placeholder,
  iconLeading,
  hint,
  errorMessage,
  size = 'md',
  fullWidth = false,
  className,
  ...props
}: TextInputProps) => (
  <TextField
    {...props}
    isInvalid={props.isInvalid ?? Boolean(errorMessage)}
    className={cx(
      'group flex flex-col gap-1.5',
      fullWidth && 'w-full',
      className,
    )}
  >
    {label && (
      <Label className={cx(typography.label, 'text-fg-secondary')}>
        {label}
        {props.isRequired && <span className="ml-0.5 text-brand-text">*</span>}
      </Label>
    )}
    <div className={cx(boxBase, size === 'sm' ? 'h-9' : 'h-10')}>
      {iconLeading && (
        <span className="inline-flex text-fg-muted">{iconLeading}</span>
      )}
      <Input
        placeholder={placeholder}
        className={cx(
          typography.body,
          'flex-1 appearance-none border-0 bg-transparent text-fg outline-none placeholder:text-fg-muted',
        )}
      />
    </div>
    {hint && !errorMessage && (
      <AriaText
        slot="description"
        className={cx(typography.body, 'text-fg-tertiary')}
      >
        {hint}
      </AriaText>
    )}
    <FieldError className={cx(typography.body, 'text-error')}>
      {errorMessage}
    </FieldError>
  </TextField>
);
